import { DerbyRngV1 } from '../rng';
import type { RaceEvent } from './types';

/**
 * The race caller: deterministic template lines off the sim's event
 * stream, with a cooldown so the booth never babbles and a priority
 * order so the wire always beats a surge.
 */

type LineKind = 'gun' | 'leadChange' | 'exhausted' | 'recovered' | 'finalFurlong' | 'wire';

const TEMPLATES: Record<LineKind, string[]> = {
  gun: ["And they're away!", 'The gates crash open!', "They're off and running!"],
  leadChange: [
    '{name} takes over at the front!',
    '{name} pokes a nose in front!',
    "Here comes {name} for the lead!",
    '{name} says not today — to the front!',
  ],
  exhausted: [
    "{name} is out of gas — the tank's empty!",
    '{name} has nothing left!',
    "The bottom's fallen out of {name}!",
  ],
  recovered: [
    '{name} finds her legs again!',
    '{name} is back in stride!',
  ],
  finalFurlong: [
    'Into the final furlong!',
    "They're in the shadow of the post!",
    'Down to the wire they come!',
  ],
  wire: [
    '{name} takes it at the wire!',
    "It's {name} by daylight — what a run!",
    '{name} gets there first!',
  ],
};

const PRIORITY: Record<LineKind, number> = {
  wire: 5,
  finalFurlong: 4,
  leadChange: 3,
  exhausted: 2,
  recovered: 1,
  gun: 4,
};

const COOLDOWN_TICKS = 75; // 2.5 s

export class CommentaryTicker {
  private readonly names: string[];
  private readonly rng: DerbyRngV1;
  private lastLineTick = -1000;
  private wireCalled = false;

  constructor(names: string[], seed: number) {
    this.names = names;
    this.rng = new DerbyRngV1(seed).fork('commentary');
  }

  private pick(kind: LineKind, lane: number | null): string {
    const template = this.rng.pick(TEMPLATES[kind]);
    return template.replace('{name}', lane !== null ? (this.names[lane] ?? 'the outsider') : '');
  }

  /** Feed each frame's events; returns a line when the booth speaks. */
  consume(events: RaceEvent[], tick: number): string | null {
    let best: { kind: LineKind; lane: number | null; priority: number } | null = null;
    for (const event of events) {
      let candidate: { kind: LineKind; lane: number | null } | null = null;
      switch (event.kind) {
        case 'gun':
          candidate = { kind: 'gun', lane: null };
          break;
        case 'leadChange':
          candidate = { kind: 'leadChange', lane: event.lane };
          break;
        case 'exhausted':
          candidate = { kind: 'exhausted', lane: event.lane };
          break;
        case 'recovered':
          candidate = { kind: 'recovered', lane: event.lane };
          break;
        case 'finalFurlong':
          candidate = { kind: 'finalFurlong', lane: null };
          break;
        case 'finish':
          if (!this.wireCalled && event.place === 1) {
            this.wireCalled = true;
            candidate = { kind: 'wire', lane: event.lane };
          }
          break;
        default:
          break;
      }
      if (candidate) {
        const priority = PRIORITY[candidate.kind];
        if (!best || priority > best.priority) best = { ...candidate, priority };
      }
    }
    if (!best) return null;
    // The wire always gets called; everything else waits out the cooldown.
    if (best.kind !== 'wire' && tick - this.lastLineTick < COOLDOWN_TICKS) return null;
    this.lastLineTick = tick;
    return this.pick(best.kind, best.lane);
  }
}

/** Photo-finish verdicts, from a time margin at the wire. */
export function verdictFor(marginS: number): string {
  if (marginS < 0.015) return 'a nose';
  if (marginS < 0.04) return 'a head';
  if (marginS < 0.1) return 'half a length';
  return 'a length';
}

/** A photo is called when the wire margin is this tight. */
export const PHOTO_MARGIN_S = 0.05;
